import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import OrganisationIcon from 'components/Svgs/icons/Organisation';
import {
  Group,
  GroupResponse,
  GroupType,
  OrganisationResponse,
  PractitionerData,
} from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { isGroupManager, isSuperAdmin } from 'utils/role';
import { styled } from '@mui/material';
import AccountOrganisationCard from 'components/OrganisationInfoCard/AccountOrganisationCard/AccountOrganisationCard';
import { createGroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import OrganisationInfoCardList from 'components/OrganisationInfoCardList';
import { sortGroupsAndOrgs } from 'utils/mappers/sorting';
import { DetailsHeader, TextV2 } from '@insights-ltd/design-library/components';
import { useAuth } from 'contexts/AuthContext';
import { INSIGHTS_ORGANISATION_ID } from 'variables';

type ItemType = 'Organisation' | 'Group';

type OrganisationOrGroupItem = {
  id: string;
  name: string;
  type: ItemType;
  allowDemotion: boolean;
  onClick: () => void;
  isPrivateOrg?: boolean;
  visibility?: OrganisationResponse['visibility'];
  canRevokeAccess?: boolean;
  parent?: Group;
  orgCount?: number;
  groupType?: GroupType;
};

type OrganisationInfoSectionProps = {
  organisations: OrganisationResponse[];
  groups?: GroupResponse[];
  practitioner?: PractitionerData;
  allowDemotion?: boolean;
  onOrganisationRemove?: (id: string) => void;
};

const StyledOrganisationIcon = styled(OrganisationIcon)(({ theme }) => ({
  width: theme.spacing(spacingSizeMap.M),
  height: theme.spacing(spacingSizeMap.M),
}));

const getListViewAllowDemotion = (
  allowDemotion: boolean,
  isPrivateOrg: boolean,
  organisationId: string,
  canRevokeAccess: boolean | undefined,
  totalDisplayedCount: number,
  isViewingOwnAccountAsSuperAdmin: boolean,
  isPractitionerSuperAdmin: boolean,
): boolean => {
  if (!allowDemotion) return false;
  if (isViewingOwnAccountAsSuperAdmin) {
    return (
      isPrivateOrg &&
      !!canRevokeAccess &&
      organisationId !== INSIGHTS_ORGANISATION_ID
    );
  }
  if (isPractitionerSuperAdmin && organisationId === INSIGHTS_ORGANISATION_ID) {
    return false;
  }
  return totalDisplayedCount > 1;
};

const getCardViewAllowDemotion = (
  allowDemotion: boolean,
  isPrivateOrg: boolean,
  organisationId: string,
  canRevokeAccess: boolean | undefined,
  totalDisplayedCount: number,
  isPractitionerAndActingUserBothSuperAdmin: boolean,
  isPractitionerSuperAdmin: boolean,
): boolean => {
  if (isPractitionerAndActingUserBothSuperAdmin && isPrivateOrg) {
    return allowDemotion && !!canRevokeAccess;
  } else if (
    isPractitionerSuperAdmin &&
    organisationId === INSIGHTS_ORGANISATION_ID
  ) {
    return false;
  }
  return allowDemotion && totalDisplayedCount > 1;
};

const OrganisationInfoSection = ({
  organisations = [],
  practitioner,
  groups = [],
  allowDemotion = false,
  onOrganisationRemove,
}: OrganisationInfoSectionProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const shouldIncludeGroups = isGroupManager(practitioner?.role);

  const organisationsInGroups = createGroupOrganisationHashMap(groups);
  const ungroupedOrganisations = shouldIncludeGroups
    ? organisations.filter(({ id }) => !organisationsInGroups[id])
    : organisations;

  const totalDisplayedCount = shouldIncludeGroups
    ? ungroupedOrganisations.length + groups.length
    : organisations.length;

  const isViewingOwnAccountAsSuperAdmin =
    isSuperAdmin(user?.role) && user?.id === practitioner?.id;
  const isPractitionerAndActingUserBothSuperAdmin =
    isSuperAdmin(user?.role) && isSuperAdmin(practitioner?.role);

  const items: OrganisationOrGroupItem[] = [
    ...ungroupedOrganisations.map((organisation) => {
      const { id, name, visibility, canRevokeAccess } = organisation;
      const isPrivateOrg = visibility === 'PRIVATE';
      return {
        id,
        name,
        type: 'Organisation' as ItemType,
        isPrivateOrg,
        visibility,
        canRevokeAccess,
        parent: organisationsInGroups[id],
        allowDemotion: getListViewAllowDemotion(
          allowDemotion,
          isPrivateOrg,
          id,
          canRevokeAccess,
          totalDisplayedCount,
          isViewingOwnAccountAsSuperAdmin,
          isSuperAdmin(practitioner?.role),
        ),
        onClick: () => {
          if (onOrganisationRemove) onOrganisationRemove(id);
        },
      };
    }),
    ...(shouldIncludeGroups
      ? groups.map((group) => {
          const { id, name, type, organisations: groupOrgs } = group;
          return {
            id,
            name,
            type: 'Group' as ItemType,
            orgCount: groupOrgs.length,
            groupType: type,
            allowDemotion: allowDemotion && totalDisplayedCount > 1,
            onClick: () => {
              if (onOrganisationRemove) onOrganisationRemove(id);
            },
          };
        })
      : []),
  ].sort(sortGroupsAndOrgs);

  let listElement;

  if (items.length > 6) {
    listElement = <OrganisationInfoCardList items={items} />;
  } else {
    listElement = items.map((organisation) => {
      const {
        id,
        name,
        orgCount,
        groupType,
        visibility,
        canRevokeAccess,
        parent,
      } = organisation;

      const isPrivateOrg = visibility === 'PRIVATE';
      if (groupType) {
        return (
          <AccountOrganisationCard
            key={id}
            id={id}
            name={name}
            type="Group"
            groupType={groupType}
            orgCount={orgCount}
            allowDemotion={allowDemotion}
          />
        );
      }

      return (
        <AccountOrganisationCard
          key={id}
          id={id}
          name={name}
          type="Organisation"
          allowDemotion={getCardViewAllowDemotion(
            allowDemotion,
            isPrivateOrg,
            id,
            canRevokeAccess,
            totalDisplayedCount,
            isPractitionerAndActingUserBothSuperAdmin,
            isSuperAdmin(practitioner?.role),
          )}
          parent={parent}
          isPrivateOrg={isPrivateOrg}
          onClick={() => {
            if (onOrganisationRemove) onOrganisationRemove(id);
          }}
        />
      );
    });
  }

  return (
    <>
      <DetailsHeader
        Icon={StyledOrganisationIcon}
        text={
          shouldIncludeGroups
            ? t('ui.event-management.my-account.organisations-and-groups', {
                count: totalDisplayedCount,
              })
            : t('ui.event-management.my-account.organisation')
        }
        id="organisation-info"
        sx={{
          marginBottom: spacingSizeMap.S,
        }}
      />
      <Grid
        container
        sx={(theme) => ({
          alignItems: 'stretch',
          paddingTop: '0.5rem',
          paddingBottom: '1.5rem',
          '& > div': {
            marginBottom: '1.0rem',
          },
          '& > div:nth-of-type(odd) > div': {
            marginRight: theme.spacing(spacingSizeMap.XS),
          },
        })}
      >
        {listElement}
        {practitioner?.id !== user?.id && isSuperAdmin(user?.role) && (
          <TextV2 variant="bodySmall">
            {t('ui.event-management.special-handling.user.info')}
          </TextV2>
        )}
      </Grid>
    </>
  );
};

export default OrganisationInfoSection;
